import { useEffect, useState, useCallback } from 'react';
import { useApi, fetchApiRef, identityApiRef, discoveryApiRef } from '@backstage/core-plugin-api';
import { useNavigate } from 'react-router-dom';
import { Progress } from '@backstage/core-components';
import { OnboardingWizard } from '@internal/plugin-onboarding-wizard';

interface Props {
  children: React.ReactNode;
}

/**
 * OnboardingGate
 *
 * Wraps the authenticated app shell. On each mount it checks the user's
 * onboarding status via /api/onboarding/status:
 *  - Guest users (no identity token): bypass the gate entirely.
 *  - Users with onboarding-complete: "true": render children as normal.
 *  - Users with onboarding-complete: "false": render the full-screen wizard
 *    instead of children; the wizard itself triggers navigation to /catalog
 *    when complete.
 */
export function OnboardingGate({ children }: Props) {
  const fetchApi = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);
  const discovery = useApi(discoveryApiRef);
  const navigate = useNavigate();

  const [checking, setChecking] = useState(true);
  const [showWizard, setShowWizard] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // Detect guest user: getBackstageIdentity() returns anonymous identity
        // with no userEntityRef for guest sessions.
        const identity = await identityApi.getBackstageIdentity();
        if (!identity?.userEntityRef || identity.userEntityRef === 'user:default/guest') {
          // Guest — skip wizard
          if (!cancelled) setChecking(false);
          return;
        }

        const baseUrl = await discovery.getBaseUrl('app');
        const res = await fetchApi.fetch(`${baseUrl}/api/onboarding/status`);
        if (!res.ok) {
          // Can't reach backend — fail open (show app, not wizard)
          if (!cancelled) setChecking(false);
          return;
        }

        const data = (await res.json()) as { complete: boolean };
        if (!cancelled) {
          setShowWizard(!data.complete);
          setChecking(false);
        }
      } catch {
        // Network error or unauthenticated — fail open
        if (!cancelled) setChecking(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fetchApi, identityApi, discovery]);

  const handleDone = useCallback(() => {
    setShowWizard(false);
    navigate('/');
  }, [navigate]);

  if (checking) {
    return <Progress />;
  }

  if (showWizard) {
    return <OnboardingWizard onDone={handleDone} />;
  }

  return <>{children}</>;
}
